import { useState, useEffect } from 'react';

const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days
const CACHE_KEY = 'cassette_avatar_cache';

export const useAvatarCache = (photoURL: string | null | undefined) => {
  const [cachedUrl, setCachedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!photoURL) {
      setCachedUrl(null);
      return;
    }

    // Check if we have a cached version
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const cache = JSON.parse(cached);
        const cachedAvatar = cache[photoURL];
        
        if (cachedAvatar) {
          const now = Date.now();
          const isExpired = now - cachedAvatar.timestamp > CACHE_DURATION;
          
          if (!isExpired) {
            // Return cached URL immediately, no network request needed
            setCachedUrl(cachedAvatar.url);
            return;
          }
        }
      }
    } catch (error) {
      // Ignore cache load issues
    }

    // No valid cache found, make network request and cache the result
    setCachedUrl(photoURL);
    
    // Cache the URL for future use
    try {
      const existingCache = localStorage.getItem(CACHE_KEY);
      const cache = existingCache ? JSON.parse(existingCache) : {};
      
      // Clean up old entries to prevent localStorage from getting too large
      const now = Date.now();
      Object.keys(cache).forEach(key => {
        if (now - cache[key].timestamp > CACHE_DURATION) {
          delete cache[key];
        }
      });
      
      cache[photoURL] = {
        url: photoURL,
        timestamp: now
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      // Ignore cache write issues
    }
  }, [photoURL]);

  return cachedUrl;
}; 